import { useEffect, useMemo, useState } from 'react';
import {
  isParticipantResponseSubmitted,
  normalizeParticipantResponseRow,
} from '../../../lib/liveSessionDisplay';
import { mergeParticipantRoster } from '../../../lib/sessionParticipantRoster';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { PrioritizationSession, SessionRankingOutput } from '../../../types/prioritization/session';
import type { ParticipantResponse } from '../../../types/prioritization/toolSession';

export interface RankedItemResult {
  itemId: string;
  rank: number;
  score: number;
  normalizedScore: number;
  criterionScores: Record<string, number>;
}

export interface ItemRankSpread {
  itemId: string;
  ranks: number[];
  medianRank: number;
  minRank: number;
  maxRank: number;
  spread: number;
  consensus: 'high' | 'medium' | 'low';
}

export interface AlignmentMetrics {
  overallPct: number;
  top5Pct: number;
  bottom5Pct: number;
  sessionAveragePct: number;
  submittedCount: number;
  totalParticipants: number;
}

export interface SessionResultsAnalytics {
  loading: boolean;
  rankedItems: RankedItemResult[];
  itemSpreads: ItemRankSpread[];
  alignment: AlignmentMetrics | null;
  participantRanksByItem: Record<string, Array<{ participantId: string; rank: number; isMe: boolean }>>;
  userId: string;
}

function totalScoreForItem(scores: Record<string, Record<string, number>>, itemId: string): number {
  const record = scores[itemId] ?? {};
  if (typeof record.total === 'number') return record.total;
  return Object.entries(record).reduce(
    (sum, [key, val]) => (key === 'total' || key === 'wins' ? sum : sum + (typeof val === 'number' ? val : 0)),
    0,
  );
}

function criterionScoresForItem(
  scores: Record<string, Record<string, number>>,
  itemId: string,
  criteriaIds: string[],
): Record<string, number> {
  const record = scores[itemId] ?? {};
  const out: Record<string, number> = {};
  for (const cid of criteriaIds) {
    const val = record[cid];
    if (typeof val === 'number' && Number.isFinite(val)) out[cid] = val;
  }
  return out;
}

function ranksFromResponse(response: ParticipantResponse): Record<string, number> {
  const ranks: Record<string, number> = {};
  for (const r of response.results ?? []) {
    if (typeof r.rank === 'number') ranks[r.id] = r.rank;
  }
  return ranks;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function alignmentPct(
  userRanks: Record<string, number>,
  consensusRanks: Record<string, number>,
  itemIds: string[],
  tolerance = 1,
): number {
  if (itemIds.length === 0) return 0;
  let matches = 0;
  for (const id of itemIds) {
    const u = userRanks[id];
    const c = consensusRanks[id];
    if (u == null || c == null) continue;
    if (Math.abs(u - c) <= tolerance) matches += 1;
  }
  return Math.round((matches / itemIds.length) * 100);
}

function buildRankedItems(
  output: SessionRankingOutput | null | undefined,
  reportRanking: Array<{ itemId: string; rank: number; score?: number }> | undefined,
  criteriaIds: string[],
): RankedItemResult[] {
  const scores = output?.scores ?? {};
  const normalized = output?.normalizedScores ?? {};

  if (output?.rankedItemIds?.length) {
    return output.rankedItemIds.map((itemId, idx) => {
      const score = totalScoreForItem(scores, itemId);
      const norm = normalized[itemId];
      return {
        itemId,
        rank: idx + 1,
        score: score || norm || 0,
        normalizedScore: typeof norm === 'number' ? norm : score,
        criterionScores: criterionScoresForItem(scores, itemId, criteriaIds),
      };
    });
  }

  if (reportRanking?.length) {
    return [...reportRanking]
      .sort((a, b) => a.rank - b.rank)
      .map(r => ({
        itemId: r.itemId,
        rank: r.rank,
        score: r.score ?? 0,
        normalizedScore: r.score ?? 0,
        criterionScores: criterionScoresForItem(scores, r.itemId, criteriaIds),
      }));
  }

  return [];
}

export function useSessionResultsAnalytics(
  session: PrioritizationSession,
  rankingOutput: SessionRankingOutput | null | undefined,
  reportRanking: Array<{ itemId: string; rank: number; score?: number }> | undefined,
): SessionResultsAnalytics {
  const userId = useUserStore(s => s.userId) ?? '';
  const [responses, setResponses] = useState<ParticipantResponse[]>([]);
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const criteriaIds = useMemo(
    () => session.criteriaConfig?.criteriaIds ?? [],
    [session.criteriaConfig?.criteriaIds],
  );

  const rankedItems = useMemo(
    () => buildRankedItems(rankingOutput, reportRanking, criteriaIds),
    [rankingOutput, reportRanking, criteriaIds],
  );

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !session.id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      const { data: toolSessions } = await supabase!
        .from('tool_sessions')
        .select('id, participant_ids')
        .eq('session_id', session.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const ts = toolSessions?.[0] as { id: string; participant_ids?: string[] } | undefined;
      if (!ts) {
        if (!cancelled) {
          setResponses([]);
          setParticipantIds(session.participantIds ?? []);
          setLoading(false);
        }
        return;
      }

      const { data: rows } = await supabase!
        .from('tool_session_responses')
        .select('*')
        .eq('tool_session_id', ts.id);

      const mapped = (rows ?? [])
        .map((r) => normalizeParticipantResponseRow(r as Record<string, unknown>))
        .filter((r) => isParticipantResponseSubmitted(r))
        .map(
          (r): ParticipantResponse => ({
            id: r.participantId,
            toolSessionId: ts.id,
            participantId: r.participantId,
            participantName: r.participantName,
            participantInitials: (r.participantName || '?')
              .split(' ')
              .map((w) => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase(),
            participantColor: '#888',
            submittedAt: new Date().toISOString(),
            results: r.results,
            isComplete: r.isComplete,
          }),
        );

      if (!cancelled) {
        setParticipantIds(
          mergeParticipantRoster(
            ts.participant_ids ?? session.participantIds ?? [],
            mapped.map((r) => r.participantId),
          ),
        );
        setResponses(mapped);
        setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [session.id, session.participantIds]);

  const analytics = useMemo(() => {
    const itemIds = rankedItems.map(r => r.itemId);

    const participantRanksByItem: Record<string, Array<{ participantId: string; rank: number; isMe: boolean }>> = {};
    const consensusRanks: Record<string, number> = {};
    const itemSpreads: ItemRankSpread[] = [];

    for (const item of rankedItems) {
      const ranks: number[] = [];
      const dots: Array<{ participantId: string; rank: number; isMe: boolean }> = [];

      for (const resp of responses) {
        const pr = ranksFromResponse(resp);
        const rank = pr[item.itemId];
        if (rank != null) {
          ranks.push(rank);
          dots.push({ participantId: resp.participantId, rank, isMe: resp.participantId === userId });
        }
      }

      if (ranks.length === 0) {
        ranks.push(item.rank);
        dots.push({ participantId: userId, rank: item.rank, isMe: true });
      }

      const med = median(ranks);
      const minR = Math.min(...ranks);
      const maxR = Math.max(...ranks);
      const spread = maxR - minR;
      consensusRanks[item.itemId] = med;

      let consensus: ItemRankSpread['consensus'] = 'high';
      if (spread >= 4) consensus = 'low';
      else if (spread >= 2) consensus = 'medium';

      itemSpreads.push({
        itemId: item.itemId,
        ranks,
        medianRank: med,
        minRank: minR,
        maxRank: maxR,
        spread,
        consensus,
      });

      participantRanksByItem[item.itemId] = dots;
    }

    let alignment: AlignmentMetrics | null = null;

    if (responses.length > 0 && itemIds.length > 0) {
      const userResp = responses.find(r => r.participantId === userId);
      const userRanks = userResp ? ranksFromResponse(userResp) : {};

      const top5 = itemIds.slice(0, 5);
      const bottom5 = itemIds.slice(-5);

      const perParticipant: number[] = [];
      for (const resp of responses) {
        perParticipant.push(alignmentPct(ranksFromResponse(resp), consensusRanks, itemIds));
      }

      alignment = {
        overallPct: alignmentPct(userRanks, consensusRanks, itemIds),
        top5Pct: alignmentPct(userRanks, consensusRanks, top5),
        bottom5Pct: alignmentPct(userRanks, consensusRanks, bottom5),
        sessionAveragePct: perParticipant.length
          ? Math.round(perParticipant.reduce((a, b) => a + b, 0) / perParticipant.length)
          : 0,
        submittedCount: responses.length,
        totalParticipants: Math.max(participantIds.length, session.participantIds?.length ?? 0, responses.length),
      };
    } else if (rankedItems.length > 0) {
      alignment = {
        overallPct: 0,
        top5Pct: 0,
        bottom5Pct: 0,
        sessionAveragePct: 0,
        submittedCount: 0,
        totalParticipants: session.participantIds?.length ?? 0,
      };
    }

    return { itemSpreads, participantRanksByItem, alignment };
  }, [rankedItems, responses, userId, participantIds, session.participantIds]);

  return {
    loading,
    rankedItems,
    itemSpreads: analytics.itemSpreads,
    alignment: analytics.alignment,
    participantRanksByItem: analytics.participantRanksByItem,
    userId,
  };
}
